import { useEffect, useState, type ReactNode } from 'react'
import { instance } from '../lib/config'

// The control-room entry (instance.entry.room; MBA Brands first, Sam 28 Oct
// 2026: "not exactly like Gulf Western's, the MBA version"). The customer's
// own world as the room: a dark navy grid, the Tasman drawn in lit dots with
// the cities and the lines between them, the four colours of the brand mark as
// the four lanes of work, the brands on a running shelf along the foot and the
// sign-in card as the console. Nothing on this page reads business data: it is
// public, so it carries names and published facts only. Styles: the .room-*
// rules in index.css.

type Room = NonNullable<NonNullable<typeof instance.entry>['room']>

// Rough coastlines (longitude, latitude), filled with a dot grid. The dots
// hide the roughness; this is a picture, not a map anyone navigates.
const LAND: [number, number][][] = [
  // Australia
  [[113.5, -22], [114, -26], [115, -34], [117.5, -35], [124, -33.5], [129, -31.5], [131.5, -31.5], [135, -34.8], [137.5, -33], [138, -35.5], [140, -38], [144, -38.3], [146.5, -39], [150, -37.5], [151.5, -33], [153.5, -28], [153, -25], [150.5, -22.5], [146, -18.5], [145.5, -15], [143.5, -11], [142.5, -10.8], [141.5, -13], [141.5, -17], [139.5, -17.5], [136.5, -15.5], [137, -12], [132.5, -11.2], [130, -13], [129.5, -15], [126, -14], [122.5, -17], [121, -19.5], [117, -20.7]],
  // Tasmania
  [[144.6, -40.7], [148.3, -40.9], [148, -43.2], [146, -43.6], [145.2, -42.2]],
  // North Island
  [[172.6, -34.4], [174.3, -35.3], [175.5, -36.8], [176, -37.6], [178.6, -37.6], [177.9, -39.2], [176.9, -39.6], [176.2, -41.3], [174.8, -41.5], [174.6, -39.8], [173.7, -39.2], [174.6, -38.2], [174.5, -36.9], [173.1, -35.3]],
  // South Island
  [[172.6, -40.4], [174.4, -41.2], [173.8, -42.4], [172.8, -43.7], [171.2, -44.4], [170.8, -45.9], [169, -46.7], [166.4, -46], [166.8, -45.1], [168.3, -44], [170.7, -42.8], [172.1, -41.3]],
]

const W0 = 112, W1 = 186, N0 = -9, S1 = -48, STEP = 0.56
const X = (lon: number) => ((lon - W0) / (W1 - W0)) * 740
const Y = (lat: number) => ((N0 - lat) / (N0 - S1)) * 390

function inside([x, y]: [number, number], poly: [number, number][]): boolean {
  let hit = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]
    const [xj, yj] = poly[j]
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit
  }
  return hit
}

const DOTS: [number, number][] = []
for (let lat = N0; lat >= S1; lat -= STEP) {
  for (let lon = W0; lon <= W1; lon += STEP) {
    if (LAND.some((p) => inside([lon, lat], p))) DOTS.push([X(lon), Y(lat)])
  }
}

const CITY: Record<string, [number, number]> = {
  Auckland: [174.76, -36.85],
  Wellington: [174.78, -41.29],
  Christchurch: [172.64, -43.53],
  Sydney: [151.2, -33.87],
  Melbourne: [144.96, -37.81],
  Brisbane: [153.03, -27.47],
  Adelaide: [138.6, -34.93],
  Perth: [115.86, -31.95],
}
const HUBS = new Set(['Auckland', 'Sydney'])

// The lines between the cities: across the Tasman in the mark's yellow and
// green, along each coast in its blue.
const ROUTES: [string, string, string][] = [
  ['Sydney', 'Auckland', '#FCC700'],
  ['Melbourne', 'Auckland', '#0FE070'],
  ['Brisbane', 'Auckland', '#FCC700'],
  ['Auckland', 'Wellington', '#4DA3FF'],
  ['Wellington', 'Christchurch', '#4DA3FF'],
  ['Sydney', 'Melbourne', '#4DA3FF'],
  ['Sydney', 'Brisbane', '#4DA3FF'],
  ['Melbourne', 'Adelaide', '#4DA3FF'],
  ['Adelaide', 'Perth', '#4DA3FF'],
]

function arc(a: string, b: string): string {
  const [x1, y1] = [X(CITY[a][0]), Y(CITY[a][1])]
  const [x2, y2] = [X(CITY[b][0]), Y(CITY[b][1])]
  const lift = Math.min(60, Math.hypot(x2 - x1, y2 - y1) * 0.28)
  return `M${x1},${y1} Q${(x1 + x2) / 2},${(y1 + y2) / 2 - lift} ${x2},${y2}`
}

function TasmanMap() {
  return (
    <svg viewBox="0 0 740 390" className="h-auto w-full" aria-hidden>
      {DOTS.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={1.55} fill="#8FB8FF" opacity={0.3} />
      ))}
      {ROUTES.map(([a, b, colour], i) => (
        <path
          key={a + b}
          d={arc(a, b)}
          fill="none"
          stroke={colour}
          strokeWidth={1.5}
          strokeLinecap="round"
          className="room-route"
          style={{ animationDelay: `${i * -0.7}s` }}
        />
      ))}
      {Object.entries(CITY).map(([name, [lon, lat]]) => {
        const hub = HUBS.has(name)
        return (
          <g key={name} transform={`translate(${X(lon)},${Y(lat)})`}>
            <circle r={hub ? 12 : 8} fill={hub ? '#FCC700' : '#4DA3FF'} className="room-ping" />
            <circle r={hub ? 4.5 : 3} fill={hub ? '#FCC700' : '#FFFFFF'} />
            {hub && (
              <text
                x={name === 'Auckland' ? 12 : -12}
                y={4}
                textAnchor={name === 'Auckland' ? 'start' : 'end'}
                className="fill-white text-[12px] font-bold"
              >
                {name}
              </text>
            )}
          </g>
        )
      })}
      <text x={X(128)} y={Y(-25)} textAnchor="middle" className="fill-white/35 text-[13px] font-extrabold uppercase tracking-[0.35em]">
        Australia
      </text>
      <text x={X(171)} y={Y(-47.6)} textAnchor="middle" className="fill-white/35 text-[11px] font-extrabold uppercase tracking-[0.3em]">
        New Zealand
      </text>
    </svg>
  )
}

function Clock({ label, tz }: { label: string; tz: string }) {
  const [now, setNow] = useState<Date | null>(null)
  useEffect(() => {
    setNow(new Date())
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])
  const time = new Intl.DateTimeFormat('en-NZ', { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', timeZone: tz })
  const date = new Intl.DateTimeFormat('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', timeZone: tz })
  return (
    <div className="text-right" aria-label={`${label} time`}>
      <p className="room-digits text-lg font-extrabold text-white sm:text-xl">{now ? time.format(now) : '--:--:--'}</p>
      <p className="mt-0.5 whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
        {now ? `${date.format(now)} · ${label}` : label}
      </p>
    </div>
  )
}

function Lane({ lane }: { lane: Room['lanes'][number] }) {
  return (
    <div className="room-lane flex items-start gap-3 px-3.5 py-3">
      <span className="mt-1 h-3 w-3 shrink-0 rounded-[3px]" style={{ background: lane.colour, boxShadow: `0 0 12px ${lane.colour}` }} aria-hidden />
      <div className="min-w-0">
        <p className="text-[14px] font-extrabold text-white">{lane.name}</p>
        <p className="text-[11.5px] leading-snug text-white/50">{lane.line}</p>
      </div>
    </div>
  )
}

function Shelf({ brands }: { brands: string[] }) {
  const row = (key: string) => (
    <div key={key} className="flex shrink-0 items-center" aria-hidden={key === 'b'}>
      {brands.map((b) => (
        <span key={b} className="flex items-center gap-6 pr-6 text-[13px] font-extrabold uppercase tracking-[0.18em] text-white/55">
          {b}
          <img src={instance.theme.logoUrl} alt="" className="h-3.5 w-3.5 opacity-70" />
        </span>
      ))}
    </div>
  )
  return (
    <div className="room-shelf overflow-hidden border-t border-white/10 bg-[#020F26]/70 py-3" aria-label="Our brands">
      <div className="room-marquee flex w-max">{[row('a'), row('b')]}</div>
    </div>
  )
}

const STRIPE = ['#05214D', '#087DFF', '#0FE070', '#FCC700']

export function LoginRoom({ card, room }: { card: ReactNode; room: Room }) {
  const { theme, displayName } = instance
  const tagline = instance.entry?.tagline

  return (
    <main className="room flex min-h-screen flex-col">
      <header className="flex items-center justify-between gap-4 border-b border-white/10 bg-[#020F26]/85 px-4 py-3 sm:px-8">
        <div className="flex items-center gap-4">
          {theme.wordmarkUrl ? (
            <span className="rounded-md bg-white px-3 py-1.5">
              <img src={theme.wordmarkUrl} alt={displayName} className="h-7 w-auto sm:h-8" />
            </span>
          ) : (
            <span className="text-lg font-extrabold text-white">{displayName}</span>
          )}
          {room.header && (
            <p className="hidden text-[11px] font-bold uppercase tracking-[0.3em] text-white/55 md:block">{room.header}</p>
          )}
        </div>
        <div className="flex items-center gap-6">
          {(room.clocks ?? []).map((c, i) => (
            <div key={c.tz} className={i === 0 ? '' : 'hidden sm:block'}>
              <Clock label={c.label} tz={c.tz} />
            </div>
          ))}
        </div>
      </header>
      <div className="flex h-[3px]" aria-hidden>
        {STRIPE.map((c) => (
          <span key={c} className="flex-1" style={{ background: c === '#05214D' ? '#163D7C' : c }} />
        ))}
      </div>

      <div className="mx-auto grid w-full max-w-7xl flex-1 items-center gap-10 px-4 py-8 sm:px-8 lg:grid-cols-[minmax(0,1fr)_25rem] lg:py-10">
        <section className="order-2 min-w-0 lg:order-1">
          <div className="hidden lg:block">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FCC700]">{displayName}</p>
            <h1 className="mt-1 text-5xl font-extrabold tracking-tight text-white">Command Centre</h1>
            {room.line && <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-white/65">{room.line}</p>}
          </div>

          <div className="relative mt-2 hidden md:block">
            <TasmanMap />
            {room.facts && room.facts.length > 0 && (
              <ul className="absolute right-0 top-[4%] space-y-2">
                {room.facts.map((f) => (
                  <li key={f.label} className="room-lane px-3.5 py-2">
                    <p className="room-digits text-xl font-extrabold text-white">{f.value}</p>
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-white/45">{f.label}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2.5 xl:grid-cols-4">
            {room.lanes.map((l) => (
              <Lane key={l.name} lane={l} />
            ))}
          </div>
        </section>

        <div className="order-1 mx-auto w-full max-w-sm lg:order-2 lg:max-w-none">
          <div className="mb-6 flex flex-col items-center text-center lg:hidden">
            {theme.logoUrl && (
              <div className="rounded-2xl bg-white p-3 shadow-[0_0_40px_rgba(8,125,255,0.35)]">
                <img src={theme.logoUrl} alt="" className="room-spin h-14 w-14" />
              </div>
            )}
            <p className="mt-4 text-xs font-bold uppercase tracking-[0.3em] text-[#FCC700]">{displayName}</p>
            <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-white">Command Centre</h1>
          </div>

          <div className="mb-6 hidden items-center gap-4 lg:flex">
            {theme.logoUrl && (
              <div className="rounded-2xl bg-white p-2.5 shadow-[0_0_40px_rgba(8,125,255,0.35)]">
                <img src={theme.logoUrl} alt="" className="room-spin h-11 w-11" />
              </div>
            )}
            {tagline && <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-white/60">{tagline}</p>}
          </div>

          <div className="relative overflow-hidden rounded-xl shadow-[0_0_70px_rgba(8,125,255,0.28)]">
            <div className="absolute inset-x-0 top-0 z-10 flex h-1.5" aria-hidden>
              {STRIPE.slice(1).map((c) => (
                <span key={c} className="flex-1" style={{ background: c }} />
              ))}
            </div>
            {card}
          </div>

          <p className="mt-5 text-center text-sm text-white/60">Need access? Ask your Command Centre administrator.</p>
        </div>
      </div>

      {room.brands && room.brands.length > 0 && <Shelf brands={room.brands} />}
    </main>
  )
}
